/**
 * NativePaymentButton
 *
 * Factory component that resolves a native payment button implementation
 * based on the `kind` identifier from the gateway registry's `nativeButton` config.
 *
 * Currently supported kinds:
 *   - 'apple-pay' → ApplePayNativeButton
 *
 * To add a new native button (e.g. Google Pay):
 *   1. Create the implementation component (e.g. GooglePayNativeButton)
 *   2. Add it to NATIVE_BUTTON_MAP below
 *   3. Set `nativeButton: { kind: 'google-pay', type: '...' }` in gatewayRegistry
 */

import React from 'react';
import ApplePayNativeButton from './ApplePayNativeButton';

// ============================================================================
// Shared interface that all native button implementations must satisfy
// ============================================================================

export interface NativePaymentButtonProps {
  type: string;
  onClick: () => void;
  className?: string;
  skeleton?: React.ReactNode;
}

// ============================================================================
// Implementation registry
// ============================================================================

const NATIVE_BUTTON_MAP: Record<string, React.FC<NativePaymentButtonProps>> = {
  'apple-pay': ApplePayNativeButton
};

// ============================================================================
// Factory component
// ============================================================================

interface Props extends NativePaymentButtonProps {
  /** Identifies the native button implementation (e.g. 'apple-pay') */
  kind: string;
}

const NativePaymentButton: React.FC<Props> = ({ kind, ...rest }) => {
  const Component = NATIVE_BUTTON_MAP[kind];
  if (!Component) return null;
  return <Component { ...rest } />;
};

export default NativePaymentButton;
